import { useState, useEffect } from 'react';
import ImageUploading, {
  ImageType as ImageType_,
  ImageUploadingPropsType,
  ErrorsType,
} from 'react-images-uploading';

import { ErrorHandler } from '@app/logger';

import styles from './PicturesUpload.module.scss';
import { IconButton } from '../buttons/IconButton';

const getSupportedFormats = () => {
  const baseFormats = ['jpg', 'jpeg', 'png'];

  return baseFormats;
};

export interface ImageType extends ImageType_ {
  uploadURL?: string;
  id?: string;
}

export type ImageListType = ImageType[];

export type PicturesUploadProps = Omit<
  ImageUploadingPropsType,
  'onChange' | 'value' | 'multiple'
> & {
  maxNumber: number;
  value?: ImageListType;
  onChange?: (value: ImageListType) => void;
  upload?: (value: ImageType) => Promise<string | null>;
  error?: string;
};

export const PicturesUpload = ({
  maxNumber,
  value,
  onChange,
  upload,
  error,
  ...props
}: PicturesUploadProps) => {
  const [pictures, setPictures] = useState<ImageListType>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorState, setErrorState] = useState<string>();

  useEffect(() => {
    if (error) {
      console.log('Error prop received:', error);
      setErrorState(error);
    }
  }, [error]);

  useEffect(() => {
    setPictures(value || []);
  }, [value]);

  const onChangeHandler = async (imageList: ImageListType) => {
    console.log('onChangeHandler called with:', imageList.length, 'images');
    console.log('Current pictures:', pictures.length);

    setIsUploading(true);

    if (!error) {
      setErrorState(undefined);
    }

    const existingPictures = pictures.filter((img) => img.uploadURL);
    const newImages = imageList.filter((img) => !img.uploadURL);
    const updatedPictures = [...existingPictures];

    console.log('Existing pictures:', existingPictures.length);
    console.log('New images to process:', newImages.length);

    updatedPictures.forEach((img) => {
      if (!img.id) {
        img.id = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
    });

    let hasErrors = false;

    for (const image of newImages) {
      if (upload) {
        if (image.file) {
          if (image.file.size > 10 * 1024 * 1024) {
            const errorMessage = 'Файл слишком большой, максимум 10 МБ';
            console.log(
              'File too large in PicturesUpload:',
              image.file.size,
              'bytes',
            );
            setErrorState(errorMessage);
            setPictures(updatedPictures);
            onChange?.(updatedPictures);
            setIsUploading(false);
            return;
          }

          if (!image.file.type.startsWith('image/')) {
            const supportedFormats = getSupportedFormats().join(', ');
            const errorMessage = `Выбранный тип файла не поддерживается. Поддерживаемые форматы: ${supportedFormats}.`;
            console.log(
              'Unsupported file type in PicturesUpload:',
              image.file.type,
            );
            console.log('Setting error state:', errorMessage);
            setErrorState(errorMessage);
            setPictures(updatedPictures);
            onChange?.(updatedPictures);
            setIsUploading(false);
            return;
          }
        }

        try {
          const uploadURL = await upload(image);

          if (!uploadURL) {
            console.error(
              'Upload returned empty URL for image:',
              image.file?.name,
            );
            hasErrors = true;
            continue;
          }

          const uploadedImage = {
            ...image,
            uploadURL: uploadURL,
            id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          };

          updatedPictures.push(uploadedImage);
        } catch (rawError) {
          console.log('Raw error in PicturesUpload:', rawError);
          const processedError: Error & { cause?: unknown } =
            rawError instanceof Error
              ? rawError
              : Object.assign(new Error('Unknown upload error'), {
                  cause: rawError,
                });
          console.log('Processed error message:', processedError.message);

          ErrorHandler.captureError(processedError, {
            component: 'PicturesUpload',
            action: 'image-upload',
            additionalData: {
              fileName: image?.file?.name,
              fileSize: image?.file?.size,
              fileType: image?.file?.type,
              originalCause: processedError?.cause,
            },
          });

          console.error(
            `Failed to upload image ${image.file?.name}:`,
            processedError,
          );
          console.log('Error message:', processedError.message);

          if (
            processedError.message.includes('слишком большой') ||
            processedError.message.includes('PDF файлы не поддерживаются') ||
            processedError.message.includes('Видео файлы не поддерживаются') ||
            processedError.message.includes('Неподдерживаемый формат файла')
          ) {
            setErrorState(processedError.message);
            setPictures(updatedPictures);
            onChange?.(updatedPictures);
            setIsUploading(false);
            return;
          } else {
            hasErrors = true;
          }
          continue;
        }
      } else {
        updatedPictures.push(image);
      }
    }

    if (updatedPictures.length > maxNumber) {
      updatedPictures.splice(maxNumber);
    }

    if (
      hasErrors &&
      updatedPictures.length === existingPictures.length &&
      !errorState
    ) {
      setErrorState(
        'Не удалось загрузить выбранные изображения. Проверьте размер файлов и попробуйте еще раз.',
      );
    }

    console.log('Final updatedPictures:', updatedPictures.length);
    console.log(
      'Updated pictures URLs:',
      updatedPictures.map((img) => img.uploadURL || img.dataURL),
    );

    setPictures(updatedPictures);
    onChange?.(updatedPictures);
    setIsUploading(false);

    if (updatedPictures.length > existingPictures.length) {
      setErrorState(undefined);
    }
  };
  const onImageClick = (index: number) => {
    setPictures((prevPictures) => {
      const updatedPictures = [...prevPictures];
      const [selectedImage] = updatedPictures.splice(index, 1);
      updatedPictures.unshift(selectedImage);
      onChange?.(updatedPictures);

      return updatedPictures;
    });
  };

  const onImageRemoveHandler = (index: number) => {
    setPictures((prevPictures) => {
      const updatedPictures = [...prevPictures];
      updatedPictures.splice(index, 1);
      onChange?.(updatedPictures);

      return updatedPictures;
    });
  };

  const onErrorHandler = (errors: ErrorsType) => {
    console.error('ImageUploading error:', errors);

    if (errors?.maxNumber) {
      setErrorState(
        'Количество выбранных изображений превышает максимально допустимое.',
      );
    } else if (errors?.acceptType) {
      const supportedFormats = getSupportedFormats().join(', ');
      setErrorState(
        `Выбранный тип файла не поддерживается. Поддерживаемые форматы: ${supportedFormats}.`,
      );
    } else if (errors?.maxFileSize) {
      setErrorState(
        'Размер выбранного файла превышает максимально допустимый (10 МБ).',
      );
    } else if (errors?.resolution) {
      setErrorState('Выбранный файл не соответствует требуемому разрешению.');
    } else {
      const errorMessage = errors
        ? Object.values(errors).join(', ')
        : 'Неизвестная ошибка';
      setErrorState(`Ошибка загрузки: ${errorMessage}`);

      ErrorHandler.captureError(new Error('ImageUploading unknown error'), {
        component: 'PicturesUpload',
        action: 'image-upload-unknown-error',
        additionalData: {
          errors,
          userAgent: navigator.userAgent,
        },
      });
    }
  };

  return (
    <ImageUploading
      {...props}
      acceptType={props.acceptType || getSupportedFormats()}
      value={[]}
      multiple={false}
      onChange={onChangeHandler}
      onError={onErrorHandler}
    >
      {({ onImageUpload }) => (
        <div
          className={styles.picturesUpload}
          data-test-id="pictures-upload-container"
        >
          <div className={styles.pictures} data-test-id="pictures-upload-list">
            {pictures.map((image: ImageType, index) => {
              const uniqueKey =
                image.id ||
                image.uploadURL ||
                image.dataURL ||
                `image-${index}-${Date.now()}`;

              return (
                <div
                  key={uniqueKey}
                  className={styles.picture}
                  data-main={index === 0}
                  data-test-id={`pictures-upload-picture-${index}`}
                  onClick={() => onImageClick(index)}
                >
                  <img
                    src={image.dataURL || image.uploadURL}
                    alt={`Uploaded image ${index + 1}`}
                    data-test-id={`pictures-upload-image-${index}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <RemoveIcon
                    className={styles.removeIcon}
                    data-test-id={`pictures-upload-remove-button-${index}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onImageRemoveHandler(index);
                    }}
                  />
                </div>
              );
            })}
            {pictures.length < maxNumber && (
              <IconButton
                testId="pictures-upload-add-button"
                type="button"
                className={styles.addButton}
                size={pictures.length > 0 ? 400 : 500}
                data-small={pictures.length > 0}
                variant="neutralSecondary"
                shape="square"
                loading={isUploading}
                onClick={() => {
                  setErrorState(undefined);
                  onImageUpload();
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </div>
          {errorState && (
            <span className={styles.error} data-test-id="pictures-upload-error">
              {errorState}
            </span>
          )}
        </div>
      )}
    </ImageUploading>
  );
};

const AddIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    fill="none"
    viewBox="0 0 64 64"
    {...props}
  >
    <path
      fill="#051732"
      fillOpacity="0.44"
      d="M32 8.002a24 24 0 1 0 24 24 24.03 24.03 0 0 0-24-24m9.23 25.846h-7.384v7.385a1.846 1.846 0 1 1-3.692 0v-7.385h-7.385a1.846 1.846 0 0 1 0-3.692h7.385V22.77a1.846 1.846 0 0 1 3.692 0v7.385h7.385a1.846 1.846 0 0 1 0 3.692"
    ></path>
  </svg>
);

const RemoveIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="none"
    viewBox="0 0 32 32"
    {...props}
  >
    <rect width="32" height="32" y="0.002" fill="#EDF8FF" rx="10"></rect>
    <path
      fill="#000D20"
      fillOpacity="0.6"
      d="M16 8.502a7.5 7.5 0 1 0 7.5 7.5 7.51 7.51 0 0 0-7.5-7.5m2.716 9.4a.577.577 0 1 1-.817.816L16 16.818l-1.9 1.9a.578.578 0 0 1-.816-.817l1.9-1.899-1.9-1.9a.577.577 0 0 1 .816-.816l1.9 1.9 1.9-1.9a.578.578 0 0 1 .816.816l-1.9 1.9z"
    ></path>
  </svg>
);
